import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { LogOut, UserPlus, Trash2, Edit, Download, FileText, Settings, RefreshCw, BellOff, Bell, Mail, FolderOpen, Database, Upload, Send, Plus, ShieldCheck, ShieldOff, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Package configurations for display
const PACKAGES = {
  trial: { name: 'Trial (1 μήνας)', color: 'bg-gray-100 text-gray-700', appLimit: 2, implLimit: 2 },
  basic: { name: 'Basic', color: 'bg-blue-100 text-blue-700', appLimit: 12, implLimit: 12 },
  professional: { name: 'Professional', color: 'bg-purple-100 text-purple-700', appLimit: 30, implLimit: 30 },
  custom: { name: 'Custom', color: 'bg-amber-100 text-amber-700', appLimit: 0, implLimit: 0 },
};

const AdminDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Project filter states
  const [appProjectFilter, setAppProjectFilter] = useState('active'); // 'active' or 'completed'
  const [implProjectFilter, setImplProjectFilter] = useState('active'); // 'active' or 'completed'
  
  // New user form
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    package_type: 'basic',
    subscription_months: 6,
    custom_application_limit: 50,
    custom_implementation_limit: 50,
  });
  
  // Edit user form
  const [editUser, setEditUser] = useState({
    email: '',
    password: '',
    package_type: '',
    subscription_months: 6,
    custom_application_limit: 50,
    custom_implementation_limit: 50,
  });
  
  // Email queue state
  const [emailQueue, setEmailQueue] = useState({
    pending: 0,
    sent_today: 0,
    failed: 0,
    upcoming: [],
    distribution: {}
  });
  const [queueLoading, setQueueLoading] = useState(false);
  
  // Renew form
  const [renewMonths, setRenewMonths] = useState(6);

  useEffect(() => {
    fetchUsers();
    fetchEmailQueue();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης χρηστών');
    }
  };

  const fetchEmailQueue = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-queue-status`);
      setEmailQueue(response.data);
    } catch (error) {
      console.error('Error fetching email queue:', error);
    }
  };

  const handleProcessQueue = async () => {
    setQueueLoading(true);
    try {
      const res = await axios.post(`${API}/admin/process-email-queue`);
      toast.success(`Queue processed! ${res.data.message || 'Τα emails θα σταλούν σταδιακά (30" ανά email)'}`);
      setTimeout(fetchEmailQueue, 3000);
    } catch (error) {
      toast.error('Σφάλμα επεξεργασίας queue: ' + (error.response?.data?.detail || error.message));
    } finally {
      setQueueLoading(false);
    }
  };

  const handleClearQueue = async () => {
    if (!window.confirm(`Είστε σίγουροι ότι θέλετε να διαγράψετε ${emailQueue.pending} pending emails;`)) return;
    try {
      await axios.delete(`${API}/admin/clear-email-queue`);
      toast.success('Η ουρά καθαρίστηκε');
      fetchEmailQueue();
    } catch (error) {
      toast.error('Σφάλμα καθαρισμού ουράς');
    }
  };

  const handleTestQueueEmail = async () => {
    const email = window.prompt('Email για δοκιμή:', 'espamanager@gmail.com');
    if (!email) return;
    try {
      const response = await axios.post(`${API}/admin/test-queue-email?email=${encodeURIComponent(email)}`);
      toast.success(`Test email στην ουρά: ${response.data.scheduled_at_greece}`);
      fetchEmailQueue();
    } catch (error) {
      toast.error('Σφάλμα δοκιμαστικού email');
    }
  };

  const handleTriggerReminders = async () => {
    setQueueLoading(true);
    try {
      const response = await axios.post(`${API}/admin/trigger-document-reminders`);
      const queued = response.data.emails_queued || 0;
      toast.success(`Reminders: ${queued} emails προστέθηκαν στην ουρά (${response.data.app_projects_checked || 0} αιτήσεις, ${response.data.impl_projects_checked || 0} υλοποιήσεις)`);
      setTimeout(fetchEmailQueue, 2000);
    } catch (error) {
      toast.error('Σφάλμα trigger reminders: ' + (error.response?.data?.detail || error.message));
    } finally {
      setQueueLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        email: newUser.email,
        password: newUser.password,
        package_type: newUser.package_type,
        subscription_months: newUser.subscription_months,
      };
      
      if (newUser.package_type === 'enterprise') {
        payload.custom_application_limit = newUser.custom_application_limit;
        payload.custom_implementation_limit = newUser.custom_implementation_limit;
      }
      
      await axios.post(`${API}/admin/users`, payload);
      toast.success('Ο χρήστης δημιουργήθηκε');
      setNewUser({
        email: '',
        password: '',
        package_type: 'basic',
        subscription_months: 6,
        custom_application_limit: 50,
        custom_implementation_limit: 50,
      });
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα δημιουργίας χρήστη');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    setLoading(true);
    try {
      const payload = {};
      if (editUser.email && editUser.email !== selectedUser.email) {
        payload.email = editUser.email;
      }
      if (editUser.password) {
        payload.password = editUser.password;
      }
      // Always send package_type for custom packages or when changed
      if (editUser.package_type === 'custom' || (editUser.package_type && editUser.package_type !== selectedUser.package_type)) {
        payload.package_type = editUser.package_type;
      }
      if (editUser.subscription_months !== selectedUser.subscription_months) {
        payload.subscription_months = editUser.subscription_months;
      }
      // Always send custom limits when package is custom
      if (editUser.package_type === 'custom') {
        payload.custom_application_limit = editUser.custom_application_limit;
        payload.custom_implementation_limit = editUser.custom_implementation_limit;
      }
      
      await axios.put(`${API}/admin/users/${selectedUser.user_id}`, payload);
      toast.success('Ο χρήστης ενημερώθηκε');
      setEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα ενημέρωσης χρήστη');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('Ο χρήστης διαγράφηκε');
      fetchUsers();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής χρήστη');
    }
  };

  const handleRenewSubscription = async () => {
    if (!selectedUser) return;
    
    try {
      await axios.post(`${API}/admin/users/${selectedUser.user_id}/renew?months=${renewMonths}`);
      toast.success(`Η συνδρομή ανανεώθηκε για ${renewMonths} μήνες`);
      setRenewDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error('Σφάλμα ανανέωσης συνδρομής');
    }
  };

  const handleToggleNotifications = async (userId, currentPaused) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/pause-notifications?paused=${!currentPaused}`);
      toast.success(currentPaused ? 'Οι ειδοποιήσεις ενεργοποιήθηκαν' : 'Οι ειδοποιήσεις σταμάτησαν');
      fetchUsers();
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  const handleToggleAiCheck = async (userId, currentEnabled) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/toggle-ai-check?enabled=${!currentEnabled}`);
      toast.success(!currentEnabled ? 'Ο αυτόματος έλεγχος ενεργοποιήθηκε' : 'Ο αυτόματος έλεγχος απενεργοποιήθηκε');
      fetchUsers();
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  const handleCheckNotifications = async () => {
    try {
      const response = await axios.post(`${API}/admin/check-subscription-notifications`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Σφάλμα αποστολής ειδοποιήσεων');
    }
  };

  const handleBackupAll = async () => {
    setBackupLoading(true);
    try {
      const response = await axios.get(`${API}/admin/backup-files`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `espa_full_backup_${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Το backup δημιουργήθηκε');
    } catch (error) {
      toast.error('Σφάλμα δημιουργίας backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleDatabaseBackup = async () => {
    setBackupLoading(true);
    try {
      const response = await axios.get(`${API}/admin/database-backup`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `espa_database_backup_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Το backup βάσης δεδομένων δημιουργήθηκε');
    } catch (error) {
      toast.error('Σφάλμα δημιουργίας backup βάσης');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleFullBackup = async () => {
    setBackupLoading(true);
    try {
      const response = await axios.get(`${API}/admin/full-backup`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `espa_full_backup_${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Το πλήρες backup δημιουργήθηκε (βάση + αρχεία)');
    } catch (error) {
      toast.error('Σφάλμα δημιουργίας backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleUserBackup = async (userId, email) => {
    try {
      const response = await axios.get(`${API}/admin/user-backup/${userId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const safeEmail = email.replace('@', '_at_').replace('.', '_');
      link.setAttribute('download', `backup_${safeEmail}_${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Backup για ${email} δημιουργήθηκε`);
    } catch (error) {
      toast.error('Σφάλμα δημιουργίας backup χρήστη');
    }
  };

  const handleUserRestore = async (userId, email, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/user-restore/${userId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Επαναφορά για ${email}: ${response.data.stats.projects_restored} έργα, ${response.data.stats.files_restored} αρχεία`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα επαναφοράς');
    }
  };

  const handleRestoreDeletedUser = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/restore-deleted-user`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.action_required === 'select_user') {
        // Multiple deleted users found - show them
        const usersList = response.data.deleted_users.map(u => `${u.email}`).join(', ');
        toast.info(`Βρέθηκαν πολλοί διαγραμμένοι χρήστες: ${usersList}. Χρησιμοποιήστε το user backup για κάθε χρήστη ξεχωριστά.`);
      } else {
        toast.success(`Ο χρήστης ${response.data.user_email} επαναφέρθηκε! ${response.data.stats.projects_restored} έργα, ${response.data.stats.files_restored} αρχεία`);
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα επαναφοράς χρήστη');
    }
  };

  const openEditDialog = (userToEdit) => {
    setSelectedUser(userToEdit);
    setEditUser({
      email: userToEdit.email,
      password: '',
      package_type: userToEdit.package_type || 'basic',
      subscription_months: userToEdit.subscription_months || 12,
      custom_application_limit: userToEdit.max_application_projects || 50,
      custom_implementation_limit: userToEdit.max_implementation_projects || 50,
    });
    setEditDialogOpen(true);
  };

  const openRenewDialog = (userToRenew) => {
    setSelectedUser(userToRenew);
    setRenewMonths(12);
    setRenewDialogOpen(true);
  };

  const getStatusBadge = (user) => {
    if (!user.days_until_expiry && user.days_until_expiry !== 0) {
      return <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">Άγνωστο</span>;
    }
    if (user.days_until_expiry < 0) {
      return <span className="px-2 py-1 rounded-full text-xs bg-rose-100 text-rose-700">Έληξε</span>;
    }
    if (user.days_until_expiry <= 15) {
      return <span className="px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-700">Λήγει σε {user.days_until_expiry}μ</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs bg-emerald-100 text-emerald-700">Ενεργή</span>;
  };

  const nonAdminUsers = users.filter(u => !u.is_admin);
  
  // Group users: owners and their employees
  const owners = nonAdminUsers.filter(u => u.role !== 'member');
  const getEmployeesForOwner = (ownerId) => {
    return nonAdminUsers.filter(u => u.owner_id === ownerId);
  };

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">Admin Panel</h2>
          <p className="text-sm text-slate-600 mt-1">{user.email}</p>
        </div>

        <nav className="p-4">
          <div className="space-y-2">
            <div className="px-4 py-2 bg-slate-100 rounded-md text-slate-900 font-medium">
              Διαχείριση Χρηστών
            </div>
            <Button
              onClick={() => navigate('/admin/templates')}
              variant="ghost"
              className="w-full justify-start"
            >
              <FileText className="mr-2 h-4 w-4" />
              Πρότυπα Αίτησης
            </Button>
            <Button
              onClick={() => navigate('/admin/implementation-templates')}
              variant="ghost"
              className="w-full justify-start"
            >
              <Settings className="mr-2 h-4 w-4" />
              Πρότυπα Υλοποίησης
            </Button>
            <Button
              onClick={() => navigate('/admin/expense-categories')}
              variant="ghost"
              className="w-full justify-start"
            >
              <FolderOpen className="mr-2 h-4 w-4" />
              Κατηγορίες Δαπανών
            </Button>
            <div className="border-t border-slate-200 my-2"></div>
            <div className="px-4 py-2 text-xs text-slate-500 uppercase tracking-wider">
              Email Queue
            </div>
            <div className="px-4 py-2 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Pending:</span>
                <span className="font-medium text-amber-600">{emailQueue.pending}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Sent today:</span>
                <span className="font-medium text-emerald-600">{emailQueue.sent_today}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Failed:</span>
                <span className="font-medium text-red-600">{emailQueue.failed}</span>
              </div>
            </div>
          </div>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <Button
            onClick={onLogout}
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-slate-900"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Αποσύνδεση
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="main-content">
        <div className="p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900">
                Διαχείριση Χρηστών
              </h1>
              <p className="text-slate-600 mt-1">
                {nonAdminUsers.length} χρήστες
              </p>
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={handleCheckNotifications}
                variant="outline"
                className="border-amber-600 text-amber-600 hover:bg-amber-50"
              >
                <Mail className="mr-2 h-4 w-4" />
                Ειδοποιήσεις
              </Button>
              
              <Button
                onClick={handleFullBackup}
                disabled={backupLoading}
                variant="outline"
                className="border-cyan-600 text-cyan-600 hover:bg-cyan-50"
              >
                <FolderOpen className={`mr-2 h-4 w-4 ${backupLoading ? 'animate-bounce' : ''}`} />
                {backupLoading ? 'Δημιουργία...' : 'Πλήρες Backup'}
              </Button>

              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".zip"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      if (window.confirm('Επαναφορά διαγραμμένου χρήστη από backup; Ο χρήστης θα δημιουργηθεί ξανά με όλα τα δεδομένα του.')) {
                        handleRestoreDeletedUser(e.target.files[0]);
                      }
                      e.target.value = '';
                    }
                  }}
                />
                <Button
                  variant="outline"
                  className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                  asChild
                >
                  <span>
                    <Upload className="mr-2 h-4 w-4" />
                    Επαναφορά Χρήστη
                  </span>
                </Button>
              </label>

              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-slate-900 hover:bg-slate-800">
                    <UserPlus className="mr-2 h-4 w-4" />
                    Νέος Χρήστης
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Νέος Χρήστης</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateUser} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password">Κωδικός</Label>
                      <Input
                        id="password"
                        type="text"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Πακέτο</Label>
                      <Select
                        value={newUser.package_type}
                        onValueChange={(value) => setNewUser({ ...newUser, package_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="trial">Trial (1 μήνας - 2+2 έργα)</SelectItem>
                          <SelectItem value="basic">Basic (12+12 έργα/εξάμηνο)</SelectItem>
                          <SelectItem value="professional">Professional (30+30 έργα/εξάμηνο)</SelectItem>
                          <SelectItem value="custom">Custom (ορίζετε εσείς)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {newUser.package_type !== 'trial' && (
                      <div className="space-y-2">
                        <Label>Διάρκεια</Label>
                        <Select
                          value={String(newUser.subscription_months)}
                          onValueChange={(value) => setNewUser({ ...newUser, subscription_months: parseInt(value) })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="6">6 Μήνες (Εξάμηνο)</SelectItem>
                            <SelectItem value="12">12 Μήνες (Έτος)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {newUser.package_type === 'custom' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Όριο Αιτήσεων</Label>
                          <Input
                            type="number"
                            value={newUser.custom_application_limit}
                            onChange={(e) => setNewUser({ ...newUser, custom_application_limit: parseInt(e.target.value) })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Όριο Υλοποιήσεων</Label>
                          <Input
                            type="number"
                            value={newUser.custom_implementation_limit}
                            onChange={(e) => setNewUser({ ...newUser, custom_implementation_limit: parseInt(e.target.value) })}
                          />
                        </div>
                      </div>
                    )}

                    {newUser.package_type !== 'trial' && newUser.package_type !== 'custom' && (
                      <div className="p-3 bg-slate-50 rounded-md text-sm text-slate-600">
                        <strong>Όρια ανά εξάμηνο:</strong>{' '}
                        {newUser.package_type === 'basic' 
                          ? '12 αιτήσεις + 12 υλοποιήσεις'
                          : '30 αιτήσεις + 30 υλοποιήσεις'
                        }
                        {newUser.subscription_months === 12 && ' (x2 για 12μηνο)'}
                      </div>
                    )}

                    <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading}>
                      {loading ? 'Δημιουργία...' : 'Δημιουργία'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Πακέτο</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Κατάσταση</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Λήξη</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                      <div className="flex items-center gap-2">
                        <span>Αιτήσεις</span>
                        <div className="flex bg-slate-200 rounded p-0.5 text-[10px]">
                          <button
                            onClick={() => setAppProjectFilter('active')}
                            className={`px-1.5 py-0.5 rounded transition-colors ${
                              appProjectFilter === 'active' ? 'bg-blue-500 text-white' : 'text-slate-600'
                            }`}
                          >
                            Ενεργά
                          </button>
                          <button
                            onClick={() => setAppProjectFilter('completed')}
                            className={`px-1.5 py-0.5 rounded transition-colors ${
                              appProjectFilter === 'completed' ? 'bg-blue-500 text-white' : 'text-slate-600'
                            }`}
                          >
                            Ολοκλ.
                          </button>
                        </div>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                      <div className="flex items-center gap-2">
                        <span>Υλοποίηση</span>
                        <div className="flex bg-slate-200 rounded p-0.5 text-[10px]">
                          <button
                            onClick={() => setImplProjectFilter('active')}
                            className={`px-1.5 py-0.5 rounded transition-colors ${
                              implProjectFilter === 'active' ? 'bg-purple-500 text-white' : 'text-slate-600'
                            }`}
                          >
                            Ενεργά
                          </button>
                          <button
                            onClick={() => setImplProjectFilter('completed')}
                            className={`px-1.5 py-0.5 rounded transition-colors ${
                              implProjectFilter === 'completed' ? 'bg-purple-500 text-white' : 'text-slate-600'
                            }`}
                          >
                            Ολοκλ.
                          </button>
                        </div>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Ειδοπ.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">AI</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Ενέργειες</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {owners.map((u) => (
                    <React.Fragment key={u.user_id}>
                      {/* Owner Row */}
                      <tr className="hover:bg-slate-50 bg-white">
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-slate-900">{u.email}</div>
                          <div className="text-xs text-slate-500">
                            {new Date(u.created_at).toLocaleDateString('el-GR')}
                            {getEmployeesForOwner(u.user_id).length > 0 && (
                              <span className="ml-2 text-blue-600">
                                ({getEmployeesForOwner(u.user_id).length} υπάλληλοι)
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${PACKAGES[u.package_type || 'basic']?.color || 'bg-gray-100'}`}>
                            {PACKAGES[u.package_type || 'basic']?.name || 'Basic'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(u)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {u.subscription_expiry 
                            ? new Date(u.subscription_expiry).toLocaleDateString('el-GR')
                            : '-'
                          }
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            {appProjectFilter === 'active' ? (
                              <>
                                <span className={`font-medium ${(u.app_projects_active || 0) >= (u.max_application_projects || 20) ? 'text-rose-600' : 'text-blue-600'}`}>
                                  {u.app_projects_active || 0}
                                </span>
                                <span className="text-slate-400">/{u.max_application_projects || 20}</span>
                              </>
                            ) : (
                              <span className="text-slate-600">{u.app_projects_completed || 0}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            {implProjectFilter === 'active' ? (
                              <>
                                <span className={`font-medium ${(u.impl_projects_active || 0) >= (u.max_implementation_projects || 20) ? 'text-rose-600' : 'text-purple-600'}`}>
                                  {u.impl_projects_active || 0}
                                </span>
                                <span className="text-slate-400">/{u.max_implementation_projects || 20}</span>
                              </>
                            ) : (
                              <span className="text-slate-600">{u.impl_projects_completed || 0}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleNotifications(u.user_id, u.notifications_paused)}
                            className={u.notifications_paused ? 'text-rose-600' : 'text-emerald-600'}
                          >
                            {u.notifications_paused ? <BellOff className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
                          </Button>
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleAiCheck(u.user_id, u.ai_check_enabled !== false)}
                            className={u.ai_check_enabled !== false ? 'text-emerald-600' : 'text-rose-600'}
                            title={u.ai_check_enabled !== false ? 'AI Έλεγχος Ενεργός' : 'AI Έλεγχος Ανενεργός'}
                            data-testid={`toggle-ai-check-${u.user_id}`}
                          >
                            {u.ai_check_enabled !== false ? <ShieldCheck className="h-4 w-4" /> : <ShieldOff className="h-4 w-4" />}
                          </Button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/admin/users/${u.user_id}/notifications`)}
                              className="text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50"
                              title="Αυτόματες Ειδοποιήσεις"
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openRenewDialog(u)}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              title="Ανανέωση"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(u)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Επεξεργασία"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUserBackup(u.user_id, u.email)}
                              className="text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50"
                              title="Backup Χρήστη"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept=".zip"
                                className="hidden"
                                onChange={(e) => {
                                  if (e.target.files[0]) {
                                    if (window.confirm(`Επαναφορά δεδομένων για ${u.email}; Αυτό θα αντικαταστήσει τα υπάρχοντα δεδομένα.`)) {
                                      handleUserRestore(u.user_id, u.email, e.target.files[0]);
                                    }
                                    e.target.value = '';
                                  }
                                }}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                title="Επαναφορά Χρήστη"
                                asChild
                              >
                                <span><RefreshCw className="h-4 w-4" /></span>
                              </Button>
                            </label>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-pink-600 hover:text-pink-700 hover:bg-pink-50"
                              title="Newsletter Credits (Drafts)"
                              onClick={async () => {
                                try {
                                  const cur = await axios.get(`${API}/newsletter/quota?user_id=${u.user_id}`);
                                  const currentTotal = cur.data?.total ?? 0;
                                  const action = window.prompt(`Newsletter Credits για ${u.email}\n\nΤρέχοντα: ${cur.data?.total ?? 0} (used: ${cur.data?.used ?? 0}, remaining: ${cur.data?.remaining ?? 0})\n\nΤι θέλετε να κάνετε;\n  - Γράψτε αριθμό για να ΡΥΘΜΙΣΕΤΕ το σύνολο (π.χ. 100)\n  - Γράψτε +Ν για να ΠΡΟΣΘΕΣΕΤΕ credits (π.χ. +50)\n  - Ακυρώστε για έξοδο`, String(currentTotal));
                                  if (action === null || action.trim() === '') return;
                                  if (action.trim().startsWith('+')) {
                                    const amt = parseInt(action.trim().slice(1));
                                    if (isNaN(amt) || amt <= 0) return toast.error('Μη έγκυρος αριθμός');
                                    const resp = await axios.post(`${API}/newsletter/admin/quota/add`, { user_id: u.user_id, amount: amt });
                                    toast.success(`Προστέθηκαν ${amt} credits. Νέο σύνολο: ${resp.data.total}`);
                                  } else {
                                    const total = parseInt(action.trim());
                                    if (isNaN(total) || total < 0) return toast.error('Μη έγκυρος αριθμός');
                                    const resp = await axios.post(`${API}/newsletter/admin/quota/set`, { user_id: u.user_id, quota: total });
                                    toast.success(`Ορίστηκαν ${total} credits συνολικά. Διαθέσιμα: ${resp.data.remaining}`);
                                  }
                                } catch (e) {
                                  toast.error('Σφάλμα διαχείρισης quota');
                                }
                              }}
                              data-testid={`quota-btn-${u.user_id}`}
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                              title="AI Generations Quota"
                              onClick={async () => {
                                try {
                                  const cur = await axios.get(`${API}/newsletter/ai-quota?user_id=${u.user_id}`);
                                  const currentTotal = cur.data?.total ?? 0;
                                  const action = window.prompt(`AI Generations για ${u.email}\n\nΌριο: πόσες φορές μπορεί να πατήσει "AI Δημιουργία" στον editor.\n\nΤρέχοντα: ${cur.data?.total ?? 0} (used: ${cur.data?.used ?? 0}, remaining: ${cur.data?.remaining ?? 0})\n\nΤι θέλετε να κάνετε;\n  - Γράψτε αριθμό για να ΡΥΘΜΙΣΕΤΕ το σύνολο (π.χ. 20)\n  - Γράψτε +Ν για να ΠΡΟΣΘΕΣΕΤΕ generations (π.χ. +10)\n  - Ακυρώστε για έξοδο`, String(currentTotal));
                                  if (action === null || action.trim() === '') return;
                                  if (action.trim().startsWith('+')) {
                                    const amt = parseInt(action.trim().slice(1));
                                    if (isNaN(amt) || amt <= 0) return toast.error('Μη έγκυρος αριθμός');
                                    const resp = await axios.post(`${API}/newsletter/admin/ai-quota/add`, { user_id: u.user_id, amount: amt });
                                    toast.success(`Προστέθηκαν ${amt} AI generations. Νέο σύνολο: ${resp.data.total}`);
                                  } else {
                                    const total = parseInt(action.trim());
                                    if (isNaN(total) || total < 0) return toast.error('Μη έγκυρος αριθμός');
                                    const resp = await axios.post(`${API}/newsletter/admin/ai-quota/set`, { user_id: u.user_id, quota: total });
                                    toast.success(`Ορίστηκαν ${total} AI generations. Διαθέσιμα: ${resp.data.remaining}`);
                                  }
                                } catch (e) {
                                  toast.error('Σφάλμα διαχείρισης AI quota');
                                }
                              }}
                              data-testid={`ai-quota-btn-${u.user_id}`}
                            >
                              <Sparkles className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                  title="Διαγραφή"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Διαγραφή Χρήστη</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Είστε σίγουροι ότι θέλετε να διαγράψετε τον χρήστη {u.email};
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteUser(u.user_id)}
                                    className="bg-rose-600 hover:bg-rose-700"
                                  >
                                    Διαγραφή
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </td>
                      </tr>
                      {/* Employee Rows */}
                      {getEmployeesForOwner(u.user_id).map((emp) => (
                        <tr key={emp.user_id} className="hover:bg-blue-50/30 bg-slate-50/50">
                          <td className="px-4 py-2 pl-8">
                            <div className="flex items-center gap-2">
                              <span className="text-slate-400">└</span>
                              <div>
                                <div className="text-sm text-slate-700">
                                  {emp.full_name || emp.email}
                                </div>
                                <div className="text-xs text-slate-400">
                                  {emp.email} • Υπάλληλος
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2">
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                              Υπάλληλος
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-slate-400">-</td>
                          <td className="px-4 py-2 text-sm text-slate-400">-</td>
                          <td className="px-4 py-2 text-sm text-slate-400">-</td>
                          <td className="px-4 py-2 text-sm text-slate-400">-</td>
                          <td className="px-4 py-2 text-sm text-slate-400">-</td>
                          <td className="px-4 py-2 text-sm text-slate-400">-</td>
                          <td className="px-4 py-2">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                  title="Διαγραφή"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Διαγραφή Υπαλλήλου</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Είστε σίγουροι ότι θέλετε να διαγράψετε τον υπάλληλο {emp.full_name || emp.email};
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteUser(emp.user_id)}
                                    className="bg-rose-600 hover:bg-rose-700"
                                  >
                                    Διαγραφή
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {owners.length === 0 && (
                    <tr>
                      <td colSpan="9" className="px-4 py-8 text-center text-slate-500">
                        Δεν υπάρχουν χρήστες
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Email Queue Section */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm mt-6">
            <div className="px-6 py-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Mail className="h-5 w-5 text-cyan-600" />
                  Email Queue
                </h2>
                <div className="flex gap-2">
                  <Button
                    onClick={handleTestQueueEmail}
                    variant="outline"
                    size="sm"
                    className="text-cyan-600 border-cyan-300"
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Test Email
                  </Button>
                  <Button
                    onClick={handleTriggerReminders}
                    disabled={queueLoading}
                    variant="outline"
                    size="sm"
                    className="text-amber-600 border-amber-300"
                  >
                    <Bell className="mr-1 h-4 w-4" />
                    Trigger Reminders
                  </Button>
                  <Button
                    onClick={handleProcessQueue}
                    disabled={queueLoading || emailQueue.pending === 0}
                    variant="outline"
                    size="sm"
                    className="text-emerald-600 border-emerald-300"
                  >
                    <Send className="mr-1 h-4 w-4" />
                    Process Now
                  </Button>
                  <Button
                    onClick={handleClearQueue}
                    disabled={emailQueue.pending === 0}
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <Trash2 className="mr-1 h-4 w-4" />
                    Clear ({emailQueue.pending})
                  </Button>
                  <Button
                    onClick={fetchEmailQueue}
                    variant="ghost"
                    size="sm"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-amber-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-amber-600">{emailQueue.pending}</div>
                  <div className="text-sm text-amber-700">Pending</div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-600">{emailQueue.sent_today}</div>
                  <div className="text-sm text-emerald-700">Sent Today</div>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-600">{emailQueue.failed}</div>
                  <div className="text-sm text-red-700">Failed</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-slate-600">
                    {(emailQueue.distribution?.['07-11'] || 0) + (emailQueue.distribution?.['11-14'] || 0) + (emailQueue.distribution?.['14-17'] || 0)}
                  </div>
                  <div className="text-sm text-slate-500">Todays Queue</div>
                </div>
              </div>

              {/* Distribution */}
              {emailQueue.distribution && (
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-sm font-medium text-slate-700">Κατανομή Ώρας (Ώρα Ελλάδας)</h3>
                    {emailQueue.current_time_greece && (
                      <span className="text-xs text-slate-500">Τρέχουσα ώρα: {emailQueue.current_time_greece}</span>
                    )}
                  </div>
                  <div className="flex gap-2 text-xs">
                    <span className="bg-cyan-100 text-cyan-700 px-2 py-1 rounded">07:00-11:00: {emailQueue.distribution['07-11'] || 0}</span>
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">11:00-14:00: {emailQueue.distribution['11-14'] || 0}</span>
                    <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">14:00-17:00: {emailQueue.distribution['14-17'] || 0}</span>
                    <span className="bg-slate-100 text-slate-700 px-2 py-1 rounded">Επόμενη μέρα: {emailQueue.distribution['next_day'] || 0}</span>
                  </div>
                </div>
              )}

              {/* Upcoming Emails */}
              {emailQueue.upcoming && emailQueue.upcoming.length > 0 ? (
                <div>
                  <h3 className="text-sm font-medium text-slate-700 mb-2">Upcoming Emails ({emailQueue.upcoming.length})</h3>
                  <div className="max-h-64 overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-slate-600">Scheduled</th>
                          <th className="px-3 py-2 text-left text-slate-600">To</th>
                          <th className="px-3 py-2 text-left text-slate-600">Subject</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailQueue.upcoming.map((email, idx) => (
                          <tr key={idx} className="border-t hover:bg-slate-50">
                            <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                              {email.scheduled_at?.substring(11, 16) || 'N/A'}
                            </td>
                            <td className="px-3 py-2 text-slate-700">
                              {email.to_email?.substring(0, 25)}{email.to_email?.length > 25 ? '...' : ''}
                            </td>
                            <td className="px-3 py-2 text-slate-600 truncate max-w-xs">
                              {email.subject?.substring(0, 50)}{email.subject?.length > 50 ? '...' : ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No pending emails in queue</p>
                </div>
              )}
            </div>
          </div>

          {/* Edit User Dialog */}
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Επεξεργασία Χρήστη</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editUser.email}
                    onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Νέος Κωδικός (κενό = δεν αλλάζει)</Label>
                  <Input
                    type="text"
                    value={editUser.password}
                    onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
                    placeholder="Αφήστε κενό για να παραμείνει ίδιος"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Πακέτο</Label>
                  <Select
                    value={editUser.package_type}
                    onValueChange={(value) => setEditUser({ ...editUser, package_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trial">Trial (1 μήνας)</SelectItem>
                      <SelectItem value="basic">Basic (12+12/εξάμηνο)</SelectItem>
                      <SelectItem value="professional">Professional (30+30/εξάμηνο)</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {editUser.package_type === 'custom' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Όριο Αιτήσεων</Label>
                      <Input
                        type="number"
                        value={editUser.custom_application_limit}
                        onChange={(e) => setEditUser({ ...editUser, custom_application_limit: parseInt(e.target.value) })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Όριο Υλοποιήσεων</Label>
                      <Input
                        type="number"
                        value={editUser.custom_implementation_limit}
                        onChange={(e) => setEditUser({ ...editUser, custom_implementation_limit: parseInt(e.target.value) })}
                      />
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditDialogOpen(false)}
                    className="flex-1"
                  >
                    Ακύρωση
                  </Button>
                  <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800" disabled={loading}>
                    {loading ? 'Αποθήκευση...' : 'Αποθήκευση'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Renew Dialog */}
          <Dialog open={renewDialogOpen} onOpenChange={setRenewDialogOpen}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Ανανέωση Συνδρομής</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Ανανέωση συνδρομής για: <strong>{selectedUser?.email}</strong>
                </p>
                
                <div className="space-y-2">
                  <Label>Διάρκεια Ανανέωσης</Label>
                  <Select
                    value={String(renewMonths)}
                    onValueChange={(value) => setRenewMonths(parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">6 Μήνες (Εξάμηνο)</SelectItem>
                      <SelectItem value="12">12 Μήνες (Έτος)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setRenewDialogOpen(false)}
                    className="flex-1"
                  >
                    Ακύρωση
                  </Button>
                  <Button
                    onClick={handleRenewSubscription}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    Ανανέωση
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
